import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useStore from '../../store/useStore';
import { packsAPI } from '../../services/api';
import { packPath } from './dataSourceFormat';
import PackLogo from './PackLogo';
import { packColor } from './packBrands';
import '../../styles/DatasetManagement.css';
import '../../styles/DataSources.css';

/**
 * The catalogue of packs: ready-made datasets, measures and KPIs for the
 * systems Kinspeed connects. Choosing one opens its pack page.
 */
const PacksPage = () => {
  const companyId = useStore((state) => state.currentCompany?.id);
  const [state, setState] = useState({ loading: true, error: '', packs: [] });

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: '' }));
    packsAPI.list(companyId)
      .then((res) => { if (!cancelled) setState({ loading: false, error: '', packs: res?.success ? res.data : [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err?.response?.data?.error || err?.message || 'Failed to load the packs', packs: [] }); });
    return () => { cancelled = true; };
  }, [companyId]);

  return (
    <div className="dataset-management">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/data-sources">Data sources</Link> / Packs
      </nav>
      <div className="page-header">
        <div>
          <h2>Packs</h2>
          <p className="page-subtitle">
            Connect the systems your company already uses. Each pack brings ready-made datasets, measures, KPIs and reports, and keeps them up to date using your own credentials.
          </p>
        </div>
      </div>

      {state.error && <div className="error-message" role="alert">{state.error}</div>}
      {state.loading ? <div className="loading-spinner" /> : (
        <ul className="pack-grid" aria-label="Packs">
          {state.packs.map((pack) => (
            <li key={pack.id}>
              <Link to={packPath(pack.id)} className="pack-card" style={{ '--pack-color': packColor(pack.id) }}>
                <span className="pack-card-head">
                  <PackLogo id={pack.id} title={pack.title} />
                  <span className="pack-card-name">
                    <strong>{pack.title}</strong>
                    {pack.category && <span className="pack-card-category">{pack.category}</span>}
                  </span>
                  {pack.installed && <span className="dataset-badge ok pack-card-installed">Installed</span>}
                </span>
                <span className="pack-card-description">{pack.description}</span>
                <span className="pack-card-counts">
                  <span className="pack-stat"><strong>{pack.datasetCount}</strong> {pack.datasetCount === 1 ? 'dataset' : 'datasets'}</span>
                  <span className="pack-stat"><strong>{pack.measureCount}</strong> {pack.measureCount === 1 ? 'measure' : 'measures'}</span>
                  <span className="pack-stat"><strong>{pack.kpiCount}</strong> {pack.kpiCount === 1 ? 'KPI' : 'KPIs'}</span>
                </span>
                <span className="pack-card-action">{pack.installed ? 'Manage' : 'View pack'} <span aria-hidden="true">→</span></span>
              </Link>
            </li>
          ))}
          {state.packs.length === 0 && <li className="dataset-empty-state">No packs are available.</li>}
        </ul>
      )}
    </div>
  );
};

export default PacksPage;
